import { Injectable, inject, signal } from "@angular/core";
import { environment } from "@environments/environment";
import { Api } from "@services/api.service";
import { Card as CardModel, CardPrice, CardSet, IndexLog, Lang } from '@models/card.model';
import { toSignal } from "@angular/core/rxjs-interop";
import { map, of, share, shareReplay } from "rxjs";
import { Payload } from "@models/common.model";
import { CardBinder } from "@models/binder.model";
import { getConditionValue } from "@components/ui/pipe/val-condition-pipe";

@Injectable({
	providedIn: 'root'
})
export class Card{
	private readonly api=inject(Api);
	private readonly cardSets$=this.getCardSets$();
	private readonly cardSets=toSignal(this.cardSets$, {initialValue: null});

	getNameFeat(card: CardModel, lang: string){
    	let name: any=null;
		if(card && card.nameFeat){
			name=card.nameFeat.find(f => f.lang==lang);

			//si aucune correspondance avec la langue alors on retourne la premiere feature trouvée
			if(!name && card.nameFeat.length)
				name=card.nameFeat[0];
		}
	    return name;
	}

	getConditionAvg(cards: CardBinder[]){
	    let avg=0;
	    cards.forEach(card => avg+=getConditionValue(card.condition));
	    if(cards.length)
		    avg/=cards.length;
	    return avg;
	}

	getRarityAvg(cards: CardBinder[], references: CardModel[]){
		let avg: number=0;
		if(cards.length){
			cards.forEach(card => {
				const ref: CardModel=references.find((r: CardModel) => r.ident==card.card);
				if(ref)
					avg+=this.getRarityValue(ref.rarity);
			});
			avg/=cards.length;
		}
		return avg;
	}

	getRarityValue(rarity: string){
		let value: number=0;

		switch(rarity){
			case 'COM':
				value=1;
				break; 
			case 'UNC':
				value=2;
				break;
			case 'RAR':
				value=3;
				break;
			case 'UR':
				value=5;
				break;
			default: 
				value=0;
				break;
		}

		return value;
	}

	//retourne l'image d'une carte dans l'index
	getImage(img: string, lang: string='fr', small: boolean=true){
		let path: string=null;
		if(img){
			if(small){
				let dotIndex: number=img.lastIndexOf('.');
				let base=img.substring(0, dotIndex);
		    	let ext=img.substring(dotIndex);
				img=`${base}-small${ext}`;
			}

			path=`${environment.imgUrl}/cards/${lang}/${img}`;
		}
		return path;
	}

	//retour toutes les images de toutes les langues
	getImages(img: string, small: boolean=true){
		return (Lang.getLangs()).map(lang => this.getImage(img, lang, small));
	}

	getLogo(img: string, lang: string='fr', small: boolean=true){
		let path: string=null;
		if(img){
			if(small)
				img+='-small';

			path=`${environment.imgUrl}/set/logo/${lang}/${img}.png`;
		}
		return path;
	}

	getLogos(img: string, small: boolean=true){
		return Lang.getLangs()
			.map(lang => this.getLogo(img, lang, small))
			.filter(i => i!=null);
	}

	getCardSetName(cardSet: CardSet, lang: string){
    	let name: string=null;
    	let langs=[lang, ...Lang.getLangs()];
    	let ind: number=0;
    	while(!name && ind<langs.length){
    		let l: string=langs[ind];
    		name=cardSet.translations.find(t => t.lang==l && t.name=='name')?.text;
    		ind++;
    	}
    	return name;
	}

	getCards(start: number, length: number=50){
		return this.api.get<Card>('/index/cards', {start, length});
	}

	getCardBySet(cardSet: string){
		return this.api.get<Card[]>(`/index/card-sets/${cardSet}`);
	}

	searchCards(q: string, start: number, length: number=50, type: string='pkm'){
		return this.api.get<Card>(`/index/cards/${type}/search`, {q, start, length});
	}

	getCard(id: string){
		return this.api.get<Card>(`/index/cards/${id}`);
	}

	getCardDetails(id: string){
		return this.api.get<CardIndexDetail>(`/index/cards/details/${id}`);
	}

	//récup des prix d'une cardIndex
	getPricesCardIndex(card: string){
		return this.api.get<CardPrice[]>(`/index/prices/${card}`);
	}

	//récup des prix d'une cardbinder
	getPricesCardBinder(card: string){
		return this.api.get<CardPrice[]>(`/my-binders/cards/${card}/prices`);
	}

	setCard(card: CardModel, images: Map<string, File>=new Map()){
		//modificaiton du nom en cas de création
		if(!card.ident)
			card.name=card.features.find(f => (f.lang=='fr' && f.name=='name')).text;

		//modification des images
		let datas: any=JSON.parse(JSON.stringify(card));
		images.forEach((file, lang) => datas[`img${lang}`]=file);

		return this.api.post<Card>(`/index/cards${((card.ident)? '/'+card.ident : '')}`, Api.toFormData(datas));
	}

	rmCard(ident: string){
		return this.api.delete(`/index/cards/${ident}`);
	}

	private getCardSets$(){
		return this.api.get<CardSet[]>('/index/card-sets')
			.pipe(
				shareReplay(1)
			);
	}

	getCardSets(){
		if(!this.cardSets())
			return this.cardSets$;
		else
			return of(this.cardSets());
	}

	getCardSet(ident: string){
		return this.getCardSets()
			.pipe(
				map(sets => sets.find((s: CardSet) => s.ident==ident))
			)
	}

	getCardSetType(type: string){
		return this.getCardSets()
			.pipe(
				map(sets => sets.filter((s: CardSet) => s.type==type))
			);
	}

	updateIndex(step: number){
		return this.api.get<Payload>(`/index/generate/${step}`);
	}

	getIndexLog(ident: string){
		return this.api.get<IndexLog>(`/index/logs/${ident}`);
	}
}

interface CardIndexDetail{
	card: Card;
	inventory: CardBinder[];
}